import React, { useState, useRef, useEffect, Fragment, useCallback } from "react";
import { useShallow } from "zustand/react/shallow";
import clsx from "clsx";

import { Button, FormControl, FormControlLabel, MenuItem, Radio, RadioGroup, Select, Tooltip } from "@mui/material";

import kutil, { type PageInfo } from "@modules/kutil";
import commonCode from "@modules/common_code";
import valueFormatter from "@modules/value_formatter";

import router from "@user/router";
import { useTabsStore } from "@user/store/tabs_store";
import { useAuthStore } from "@user/store/auth_store";
import { useSettingsStore } from "@user/store/settings_store";

import "./cell_monitoring.scoped.scss";

import SkuInfoPopup from "@user/views/popup/popup_sku_info";

interface State {
  isContinueSearch: boolean,
  isShowSkuInfo: boolean,
  workStationId: string,
  cellType: string,
  location3: number,
  location4: number,
}

const defState: State = {
  isContinueSearch: true,
  isShowSkuInfo: false,
  workStationId: "",
  cellType: "BUCKET",
  location3: kutil.LOCATION_RANGE.level3.wb,
  location4: kutil.LOCATION_RANGE.level4.wb,
};

interface SearchParams {
  location12: string,
  searchType: string,
  page: number,
  pageSize: number
}

const defSearchParams: SearchParams = {
  location12: "01001",
  searchType: "STATUS",
  page: 1,
  pageSize: kutil.DEFAULT_PAGE_SIZE
};

interface Result {
  searchParams: SearchParams,
  pageInfo: PageInfo,
  infos: any[],
  cellMap: Record<string, any>,
}

const defResult: Result = {
  searchParams: kutil.deepCopy(defSearchParams),
  pageInfo: {
    totalCount: 0,
    totalPage: 0,
    page: defSearchParams.page,
    pageSize: defSearchParams.pageSize
  },
  infos: [],
  cellMap: {},
};

interface Info {
  locationId: string,
  disableCellYn: string,
  locationIdName: string,
  cellStatusName: string,
  skuNo: string,
  skuNoExName: string,
  skuTypeName: string,
  skuSizeName: string,
  skuCellName: string,
  loadRateName: string,
  workStationId: string,
  outPossYn: string
}

const defInfo = {
  locationId: "",
  disableCellYn: "",
  locationIdName: "",
  cellStatusName: "",
  skuNo: "",
  skuNoExName: "",
  skuTypeName: "",
  skuSizeName: "",
  skuCellName: "",
  loadRateName: "",
  workStationId: "",
  outPossYn: ""
};

const validationMsg4updateCell = [
  {id: "locationId", type: "NotBlank", msg: "CELL 위치를 확인해 주세요."}
];

const validationMsg4reqSkuPick = [
  {id: "reqInfos", type: "NotBlank", msg: "지정출고 CELL을 선택해 주세요."},
  {id: "workStationId", type: "NotBlank", msg: "출고작업대를 선택해 주세요."},
  {id: "skuNo", type: "InvalidStatus", msg: "현재 작업대에서 호출할 수 없는 SKU입니다."}
];

function CellMonitoring(props: any) {
  const [state, setState] = useState<State>(kutil.deepCopy(defState));
  const [searchParams, setSearchParams] = useState<SearchParams>(kutil.deepCopy(defSearchParams));
  const [result, setResult] = useState<Result>(kutil.deepCopy(defResult));
  const [info, setInfo] = useState<Info>(kutil.deepCopy(defInfo));

  const auth = useAuthStore(useShallow((state) => state.getAuth(props.id)));
  const isActiveTab = useTabsStore((state) => state.currTabId == props.id);
  const location = useTabsStore((state) => state.getLocation(props.id));

  const searchParamsRef = useRef(searchParams);
  const updateSearchParams = useCallback((patch: Partial<SearchParams>, isReset: boolean = false) => {
    if (isReset) {
      searchParamsRef.current = { ...patch } as SearchParams;
    } else {
      searchParamsRef.current = { ...searchParamsRef.current, ...patch };
    }
    setSearchParams(searchParamsRef.current);
    return searchParamsRef.current;
  }, []);

  // location 변경시 처리
  useEffect(() => {
    init();
  }, [location.key]);

  // 바코드 스캔 기능 적용
  const onBarcodeScanRef = useRef(onBarcodeScan);
  useEffect(() => {
    onBarcodeScanRef.current = onBarcodeScan;
  });

  useEffect(() => {
    if (!isActiveTab || state.isShowSkuInfo) {
      return;
    }

    const handler = (code: string) => onBarcodeScanRef.current(code);
    kutil.startBarcodeScan(handler);
    return () => kutil.stopBarcodeScan(handler);
  }, [isActiveTab, state.isShowSkuInfo]);
  // 끝: 바코드 스캔 기능 적용

  // 작업대ID 기본값 지정
  useEffect(() => {
    const workStationId = useSettingsStore.getState().getDefWorkStationId(location.path) || (commonCode.workStationId as any[])?.[0]?.code || "";
    setState(prev => ({ ...prev, workStationId}));
  }, [location.path]);

  // 변경 작업대ID 저장
  useEffect(() => {
    if (state.workStationId) {
      useSettingsStore.getState().setLocalSetting("workStationId", location.path, state.workStationId);
    }
  }, [state.workStationId]);

  useEffect(() => {
    if (!isActiveTab) {
      return;
    }

    if (state.isContinueSearch) {
      const intervalId = setInterval(() => {
        searchInfos();
      }, 5000);

      return () => clearInterval(intervalId);
    }
  }, [state.isContinueSearch, isActiveTab]);

  useEffect(() => {
    if (searchParams.location12.substring(0,2) == "06" || searchParams.location12.substring(0,2) == "07") {
      setState(prev => ({ ...prev,
        cellType: "PALLET",
        location3: kutil.LOCATION_RANGE.level3.wp,
        location4: kutil.LOCATION_RANGE.level4.wp,
      }));
    } else {
      setState(prev => ({ ...prev,
        cellType: "BUCKET",
        location3: kutil.LOCATION_RANGE.level3.wb,
        location4: kutil.LOCATION_RANGE.level4.wb,
      }));
    }
  }, [searchParams.location12]);

  const isInit = useRef(false);
  async function init() {
    try {
      if (isInit.current == false) {
        isInit.current = await initData();
      }
      if (isInit.current) {
        await initParams();
        await searchInfos();
      }
    } finally {
      kutil.setLoadingStatus(false);
    }
  }

  async function initData() {
    return true;
  }

  async function initParams() {
    const searchParams = kutil.deepCopy(defSearchParams);
    kutil.copyObj(Object.fromEntries(new URLSearchParams(location.search).entries()), searchParams);
    updateSearchParams(searchParams);
  }

  async function searchInfos() {
    const searchParams = searchParamsRef.current;

    try {
      const reqSearchParams = kutil.copyObj(searchParams, {} as SearchParams);
      const data = await kutil.sendGet("/api/stock/cell_monitoring.data?mode=search", reqSearchParams, {});
      for (const info of data.infos) {
        info.locationIdName = valueFormatter.locationId(info.locationId);
        let cellStatusName = "공CELL";
        if (info.disableCellYn === "Y") cellStatusName = "금지CELL";
        else if (info.workStatusEquip === "ERROR") cellStatusName = "오류CELL";
        else if (info.skuNo) {
          if (info.inoutType === "I") cellStatusName = "입고작업";
          else if (info.inoutType === "O") cellStatusName = "출고작업";
          else if (info.workType === "A") cellStatusName = "실사작업";
          else if (Number(info.stockQty) > 0) cellStatusName = "적재SKU";
          else if (Number(info.stockQty) == 0) cellStatusName = "공SKU";
        }
        info.cellStatusName = cellStatusName;
        info.skuNoExName = info.skuNo ? info.skuNoEx : "-";
        info.skuTypeName = info.skuNo ? commonCode.getCodeValue("skuType", info.skuType) : "-";
        info.skuSizeName = info.skuNo ? commonCode.getCodeValue("skuSize", info.skuSize) : "-";
        info.skuCellName = info.skuNo ? [info.skuCellUseQty, info.skuCellQty].filter(Boolean).join("/") : "-";
        info.loadRateName = info.skuNo ? `${100 - Number(info.stockRate ?? 0)}%` : "-";
      }

      const cellMap: Record<string, any> = {};
      data.infos.forEach((item: any) => {
        const x = parseInt(item.locationId.substring(5, 8));
        const y = parseInt(item.locationId.substring(8, 10));
        cellMap[`${x}_${y}`] = item;
      });

      setResult({
        searchParams: reqSearchParams,
        pageInfo: data.pageInfo,
        infos: data.infos,
        cellMap: cellMap,
      });

      setInfo(prev => {
        if (!prev.locationId) return prev;
        const newInfo = data.infos.find((d: any) => d.locationId === prev.locationId);
        return newInfo ? { ...prev, ...newInfo } : prev;
      });
    } catch(e) {
      console.log(e);
    }
  }

  async function doSearch(_e: any, reqParams: any|null = null) {
    setState(prev => ({ ...prev, isContinueSearch: false }));

    const searchParams = updateSearchParams({ ...reqParams, page: 1 });
    router.navigateByPath("/stock/cell_monitoring", searchParams);
  }

  const goPage = useCallback((page: number) => {
    const searchParams = updateSearchParams({ ...result.searchParams, page }, true);
    router.navigateByPath("/stock/cell_monitoring", searchParams);
  }, [result, router, updateSearchParams]);

  async function doSearchContinue(search: boolean) {
    setState(prev => ({ ...prev, isContinueSearch: search }));
    if (search) {
      await searchInfos();
    }
  }

  const showDetail = useCallback(async (info: any) => {
    setState(prev => ({ ...prev, isContinueSearch: false }));

    setInfo(prev => ({ ...prev, ...info }));
  }, []);

  async function updateDisableCellYn(disableCellYn: string) {
    setState(prev => ({ ...prev, isContinueSearch: false }));

    const isValid = await kutil.checkValidation(info, validationMsg4updateCell);
    if (isValid == false) {
      return;
    }

    const confirmMsg = disableCellYn == "Y" ? "금지CELL설정" : "금지CELL해제";
    if (await kutil.confirm(confirmMsg + "하시겠습니까?") == false) {
      return false;
    }

    try {
      const reqInfo = { ...info, disableCellYn };
      const data = await kutil.sendPost("/api/stock/cell_monitoring.proc?mode=updateDisableCellYn", reqInfo, {
        validationMsg: validationMsg4updateCell
      });
      if (disableCellYn == "Y") {
        await kutil.alert("설정하였습니다.");
      } else {
        await kutil.alert("해제하였습니다.");
      }
      router.reload();
    } catch(e) {
      console.log(e);
    }
  }

  async function showSkuInfo() {
    openPopupSkuInfo();
  }
 
  async function reqSkuPick() {
    setState(prev => ({ ...prev, isContinueSearch: false }));

    if (!state.workStationId) {
      kutil.alert("출고작업대를 선택해 주세요.");
      return;
    }

    if (await kutil.confirm("지정출고하시겠습니까?") == false) {
      return false;
    }

    try {
      const reqInfos = [info];
      reqInfos[0].workStationId = state.workStationId;
      const data = await kutil.sendPost("/api/stock/cell_monitoring.proc?mode=reqSkuPick", reqInfos, {
        validationMsg: validationMsg4reqSkuPick
      });
      await kutil.alert("지정출고하였습니다.");
      router.reload();
    } catch(e) {
      console.log(e);
    }
  }

  async function openPopupSkuInfo() {
    setState(prev => ({ ...prev, isContinueSearch: false, isShowSkuInfo: true }));
  }

  async function closePopupSkuInfo(_data: any) {
    setState(prev => ({ ...prev, isShowSkuInfo: false }));
  }

  async function onBarcodeScan(code: string) {
    const [prefix, ...rest] = code.split(":");
    const value = rest.join(":").trim();

    if (rest.length > 0) {
      switch (prefix) {
        case "WS": {
          if (!value) return;
          const exists = (commonCode.workStationId as any[]).some(c => c.code === value);
          if (!exists) {
            kutil.alert("일치하는 작업대가 없습니다.\r\n" + code);
            return;
          }
          setState(prev => ({ ...prev, workStationId: value }));
          return;
        }
        default:
          return;
      }
    }
  }

  return (
    <>
      {/* contents */}
      <div className="contents">
        {/* contents 상단 */}
        <div className="contents-top">
          {/* 타이틀 영역 */}
          <div className="title-area">
            {/* 타이틀 */}
            <p>CELL 모니터링</p>
          </div>
          {/*// 타이틀 영역 */}
          {/* 버튼 영역 */}
          <div className="top-btn-area">
            <Button variant="outlined" onClick={doSearch}>조회</Button>
            {state.isContinueSearch == true && <Button variant="outlined" onClick={() => doSearchContinue(false)}>연속조회중지</Button>}
            {state.isContinueSearch == false && <Button variant="outlined" onClick={() => doSearchContinue(true)}>연속조회시작</Button>}
          </div>
          {/*// 버튼 영역 */}
        </div>
        {/*// contents 상단 */}
        {/* CELL 모니터링 정보 */}
        <div className="monitoring-con">
          {/* CELL 정보 */}
          <div className={state.cellType == "PALLET" ? "cell-pallet" : "cell-bucket"}>
            <span></span>
            {Array.from({ length: state.location3 }, (_, i) => (
              <span key={i}>X{i + 1}</span>
            ))}

            {(() => {
              return Array.from({ length: state.location4 }, (_, yi) => {
                const y = state.location4 - yi;
                return (
                  <Fragment key={y}>
                    <span>Y{y}</span>
                    {Array.from({ length: state.location3 }, (_, xi) => {
                      const x = xi + 1;
                      const cell = result.cellMap[`${x}_${y}`];
                      return (
                        <Cell
                          type={state.cellType}
                          key={x + "_" + y}
                          searchType={searchParams.searchType}
                          cell={cell}
                          x={x}
                          y={y}
                          isActive={info.locationId == cell?.locationId}
                          onClick={showDetail}
                        />
                      );
                    })}
                  </Fragment>
                );
              });
            })()}
          </div>
          {/*// CELL 정보 */}
          {/* 스태커 버튼 */}
          <div className="stc-button">
            {[
              { label: "1-1", value: "01001" },
              { label: "1-2", value: "01002" },
              { label: "2-1", value: "02001" },
              { label: "2-2", value: "02002" },
              { label: "3-1", value: "03001" },
              { label: "3-2", value: "03002" },
              { label: "4-1", value: "04001" },
              { label: "4-2", value: "04002" },
              { label: "5-1", value: "05001" },
              { label: "5-2", value: "05002" },
              { label: "6-1", value: "06001" },
              { label: "6-2", value: "06002" },
              { label: "7-1", value: "07001" },
              { label: "7-2", value: "07002" },
            ].map(({ label, value }) => (
              <Button
                key={value}
                variant="additional"
                className={searchParams.location12 === value ? "active" : ""}
                onClick={(e) => doSearch(e, { location12: value })}
              >
                {label}
              </Button>
            ))}
          </div>
          {/*// 스태커 버튼 */}
          {/* CELL 상세 정보 */}
          <div className="cell-info">
            {/* 기본정보 */}
            <div className="table-wrap">
              <table>
                <colgroup>
                  <col style={{ width: "40%" }} />
                  <col style={{ width: "60%" }} />
                </colgroup>
                <tbody>
                  <tr>
                    <th scope="row">CELL 위치</th>
                    <td>{ info.locationIdName }</td>
                  </tr>
                  <tr>
                    <th scope="row">CELL 상태</th>
                    <td>{ info.cellStatusName }</td>
                  </tr>
                  <tr>
                    <th scope="row">SKU No</th>
                    <td>{ info.skuNoExName }</td>
                  </tr>
                  <tr>
                    <th scope="row">SKU Size</th>
                    <td>{ info.skuSizeName }</td>
                  </tr>
                  <tr>
                    <th scope="row">SKU Cell</th>
                    <td>{ info.skuCellName }</td>
                  </tr>
                  <tr>
                    <th scope="row">적재 가능률</th>
                    <td>{ info.loadRateName }</td>
                  </tr>
                </tbody>
              </table>
            </div>
            {/* 기본정보 */}
            <div className="cell-info-btn">
              {info.locationId && info.disableCellYn !== "Y" && auth.write &&
              <Button variant="outlined" autoFocus onClick={() => updateDisableCellYn("Y")}>
                금지Cell설정
              </Button>
              }
              {info.locationId && info.disableCellYn === "Y" && auth.write &&
              <Button variant="outlined" autoFocus onClick={() => updateDisableCellYn("N")}>
                금지Cell해제
              </Button>
              }
              {info.skuNo &&
              <Button variant="outlined" autoFocus onClick={showSkuInfo}>
                상세조회
              </Button>
              }
            </div>
            <em></em>
            {/* 줄고작업대 */}
            <div className="sku-outbound">
              <p>출고작업대</p>
              <FormControl size="small">
                <Select
                  value={state.workStationId}
                  onChange={(e) => {setState(prev => ({ ...prev, workStationId: e.target.value }))}}
                  displayEmpty
                >
                {(commonCode.workStationId as any[]).map((code) => (
                  <MenuItem key={code.code} value={code.code}>{code.value}</MenuItem>
                ))}
                </Select>
              </FormControl>
              {info.skuNo && info.outPossYn == "Y" && auth.write &&
              <Button variant="additional" onClick={reqSkuPick}>지정출고</Button>
              }
            </div>
            <em></em>
            {/* 범례 */}
            <div className="view-option">
              <p>조회구분</p>
              <div className="form-base">
                <FormControl>
                  <RadioGroup
                    value={searchParams.searchType}
                    onChange={(e) => {doSearch(e, {searchType: e.target.value})}}
                    row
                  >
                    <FormControlLabel value="STATUS" control={<Radio size="small" />} label="상태" />
                    <FormControlLabel value="RATE" control={<Radio size="small" />} label="적재가능률" />
                  </RadioGroup>
                </FormControl>
              </div>
              {/* 범례 내용 - 상태 */}
              {searchParams.searchType === "STATUS" && (
                <div className="legend cell-status">
                  <p>범례</p>
                  <div>
                    <Tooltip title="자재가 있는 SKU가 위치한 셀">
                      <div>
                        <em className="cell-occupied"></em>
                        적재SKU
                      </div>
                    </Tooltip>
                    <Tooltip title="자재가 없는 SKU가 위치한 셀">
                      <div>
                        <em className="cell-empty"></em>
                        공SKU
                      </div>
                    </Tooltip>
                    <Tooltip title="입고작업 지시가 내려진 셀">
                      <div>
                        <em className="cell-inbound"></em>
                        입고작업
                      </div>
                    </Tooltip>
                    <Tooltip title="출고작업 지시가 내려진 셀">
                      <div>
                        <em className="cell-outbound"></em>
                        출고작업
                      </div>
                    </Tooltip>
                    <Tooltip title="실사작업 지시가 내려진 셀">
                      <div>
                        <em className="cell-cycle-count"></em>
                        실사작업
                      </div>
                    </Tooltip>
                    <Tooltip title="팔레트/버킷이 없는 셀">
                      <div>
                        <em className="cell-no-sku"></em>
                        공CELL
                      </div>
                    </Tooltip>
                    <Tooltip title="금지 CELL로 지정된 셀">
                      <div>
                        <em className="cell-blocked"></em>
                        금지CELL
                      </div>
                    </Tooltip>
                    <Tooltip title="오류가 발생한 셀">
                      <div>
                        <em className="cell-error"></em>
                        오류CELL
                      </div>
                    </Tooltip>
                    <Tooltip title="로케이션이 지정되지 않은 공간">
                      <div>
                        <em className="non-location"></em>
                        미지정
                      </div>
                    </Tooltip>
                  </div>
                </div>
              )}
              {/* 범례 내용 - 적재가능률 */}
              {searchParams.searchType === "RATE" && (
                <div className="legend cell-rate">
                  <p>범례</p>
                  <div>
                    <Tooltip title="적재가능률 100% 셀">
                      <div>
                        <em className="cell-rate-1"></em>
                        100%
                      </div>
                    </Tooltip>
                    <Tooltip title="적재가능률 80% 이상 100% 미만 셀">
                      <div>
                        <em className="cell-rate-2"></em>
                        80~100%
                      </div>
                    </Tooltip>
                    <Tooltip title="적재가능률 60% 이상 80% 미만 셀">
                      <div>
                        <em className="cell-rate-3"></em>
                        60~80%
                      </div>
                    </Tooltip>
                    <Tooltip title="적재가능률 40% 이상 60% 미만 셀">
                      <div>
                        <em className="cell-rate-4"></em>
                        40~60%
                      </div>
                    </Tooltip>
                    <Tooltip title="적재가능률 20% 이상 40% 미만 셀">
                      <div>
                        <em className="cell-rate-5"></em>
                        20~40%
                      </div>
                    </Tooltip>
                    <Tooltip title="적재가능률 0% 초과 20% 미만 셀">
                      <div>
                        <em className="cell-rate-6"></em>
                        0~20%
                      </div>
                    </Tooltip>
                    <Tooltip title="적재가능률 0% 셀">
                      <div>
                        <em className="cell-rate-7"></em>
                        0%
                      </div>
                    </Tooltip>
                    <Tooltip title="팔레트/버킷이 없는 셀">
                      <div>
                        <em className="cell-no-sku"></em>
                        공CELL
                      </div>
                    </Tooltip>
                    <Tooltip title="금지 CELL로 지정된 셀">
                      <div>
                        <em className="cell-blocked"></em>
                        금지CELL
                      </div>
                    </Tooltip>
                    <Tooltip title="오류가 발생한 셀">
                      <div>
                        <em className="cell-error"></em>
                        오류CELL
                      </div>
                    </Tooltip>
                    <Tooltip title="로케이션이 지정되지 않은 공간">
                      <div>
                        <em className="non-location"></em>
                        미지정
                      </div>
                    </Tooltip>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
        {/*// CELL 모니터링 정보 */}
        {state.isShowSkuInfo && <SkuInfoPopup isShow={state.isShowSkuInfo} onClose={closePopupSkuInfo} data={{skuNo: info.skuNo}} />}
      </div>
      {/*// contents */}
    </>
  );
}

const Cell = React.memo(
  ({type, searchType, cell, x, y, isActive, onClick}: any) => {
    // 로케이션 미지정 공간 (tb_location 에 행이 없음)
    if (!cell) {
      return <p className="non-location" />;
    }

    let cls = "cell-no-sku";
    if (cell.disableCellYn === "Y") cls = "cell-blocked";
    else if (cell.workStatusEquip === "ERROR") cls = "cell-error";
    else if (searchType === "STATUS") {
      if (cell.skuNo) {
        if (cell.inoutType === "I") cls = "cell-inbound";
        else if (cell.inoutType === "O") cls = "cell-outbound";
        else if (cell.workType === "A") cls = "cell-cycle-count";
        else if (Number(cell.stockQty) > 0) cls = "cell-occupied";
        else if (Number(cell.stockQty) == 0) cls = "cell-empty";
      }
    } else if (searchType === "RATE") {
      if (cell.skuNo) {
        const loadRate = 100 - Number(cell.stockRate ?? 0);
        if (loadRate == 100) cls = "cell-rate-1";
        else if (loadRate >= 80) cls = "cell-rate-2";
        else if (loadRate >= 60) cls = "cell-rate-3";
        else if (loadRate >= 40) cls = "cell-rate-4";
        else if (loadRate >= 20) cls = "cell-rate-5";
        else if (loadRate > 0) cls = "cell-rate-6";
        else cls = "cell-rate-7";
      }
    }

    return (
      <p
        data-tooltip={`X${x}, Y${y}`}
        className={clsx(
          cls,
          "cell-with-tooltip",
          type == "BUCKET" && y <= 3 && "tooltip-top",
          type == "PALLET" && y <= 1 && "tooltip-top",
          isActive && "active",
        )}
        onClick={() => onClick(cell)}
      />
    )
  }
);

export default React.memo(CellMonitoring);
